import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Print as PrintIcon, ArrowBack as BackIcon } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Screen and paper both render the same A4 sheet; only the toolbar is screen-only.
const PRINT_CSS = `
@page { size: A4; margin: 14mm; }
@media print {
  body { background: #fff !important; }
  .no-print { display: none !important; }
  .sheet { box-shadow: none !important; margin: 0 !important; width: auto !important;
           min-height: auto !important; padding: 0 !important; }
}
`;

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
};

function Section({ title, children }) {
  return (
    <Box sx={{ mb: 2.5, breakInside: 'avoid' }}>
      <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: '#0f766e', textTransform: 'uppercase', mb: 0.75 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

/**
 * The patient's copy of a completed visit, on the practice's letterhead.
 *
 * Header and footer come from the letterhead the office uses (its own, else the nearest
 * above it, else its stored name and address). The last line is always "Powered by
 * ScribeLive" — the server sends it and nothing here lets it be removed.
 */
export default function PrintVisit() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/v1/clinical/notes/${sessionId}/print`)
      .then(({ data: d }) => setData(d))
      .catch((err) => setError(err.response?.data?.detail || 'Could not load this visit for printing.'));
  }, [sessionId]);

  if (error) {
    return (
      <Box sx={{ p: 4, maxWidth: 640, mx: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate(-1)}>Back</Button>
      </Box>
    );
  }
  if (!data) return <Box sx={{ p: 8, textAlign: 'center' }}><CircularProgress /></Box>;

  const lh = data.letterhead || {};
  const contact = [lh.phone, lh.email, lh.website].filter(Boolean);
  const rx = data.prescriptions || [];
  const codes = data.icd10_codes || [];

  return (
    <Box sx={{ bgcolor: '#e5e7eb', minHeight: '100vh', py: 3 }}>
      <style>{PRINT_CSS}</style>

      <Stack direction="row" spacing={1.5} justifyContent="center" className="no-print" sx={{ mb: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(-1)}>Back</Button>
        <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}>
          Print
        </Button>
      </Stack>

      <Box className="sheet" sx={{
        bgcolor: '#fff', width: '210mm', minHeight: '297mm', mx: 'auto', p: '16mm',
        boxShadow: '0 10px 30px rgba(0,0,0,0.12)', color: '#111827',
        display: 'flex', flexDirection: 'column', fontFamily: '"Outfit", system-ui, sans-serif',
      }}>
        {/* Letterhead */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, pb: 2, borderBottom: '2px solid #0f766e' }}>
          {data.logo && (
            <Box component="img" src={data.logo} alt="" sx={{ maxHeight: 64, maxWidth: 180, objectFit: 'contain' }} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>{lh.practice_name}</Typography>
            {lh.address && <Typography sx={{ fontSize: 12.5, color: '#374151', whiteSpace: 'pre-line' }}>{lh.address}</Typography>}
            {!!contact.length && <Typography sx={{ fontSize: 12.5, color: '#374151' }}>{contact.join('  ·  ')}</Typography>}
            {lh.registration && <Typography sx={{ fontSize: 11.5, color: '#6b7280' }}>{lh.registration}</Typography>}
            {lh.header_note && <Typography sx={{ fontSize: 11.5, color: '#6b7280', fontStyle: 'italic' }}>{lh.header_note}</Typography>}
          </Box>
        </Box>

        {/* Patient and visit */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, my: 2.5, p: 1.5, bgcolor: '#f3f4f6', borderRadius: 1 }}>
          <Typography sx={{ fontSize: 13 }}><b>Patient:</b> {data.patient?.name}</Typography>
          <Typography sx={{ fontSize: 13 }}><b>Date of visit:</b> {fmtDate(data.visit?.date)}</Typography>
          <Typography sx={{ fontSize: 13 }}><b>Date of birth:</b> {fmtDate(data.patient?.date_of_birth) || '—'}</Typography>
          <Typography sx={{ fontSize: 13 }}>
            <b>Doctor:</b> {data.visit?.doctor_name}
            {data.visit?.doctor_speciality?.length ? ` (${data.visit.doctor_speciality.join(', ')})` : ''}
          </Typography>
        </Box>

        <Box sx={{ flex: 1 }}>
          {(data.assessment || codes.length > 0) && (
            <Section title="Diagnosis">
              {data.assessment && <Typography sx={{ fontSize: 13.5, whiteSpace: 'pre-wrap' }}>{data.assessment}</Typography>}
              {codes.length > 0 && (
                <Typography sx={{ fontSize: 12, color: '#4b5563', mt: 0.5 }}>
                  {codes.map((c) => (typeof c === 'string' ? c : `${c.code}${c.description ? ` — ${c.description}` : ''}`)).join('; ')}
                </Typography>
              )}
            </Section>
          )}

          {rx.length > 0 && (
            <Section title="Prescription">
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
                <tbody>
                  {rx.map((r, i) => (
                    <tr key={i}>
                      <Box component="td" sx={{ py: 0.75, pr: 2, width: 28, color: '#0f766e', fontWeight: 800, verticalAlign: 'top' }}>℞</Box>
                      <Box component="td" sx={{ py: 0.75, borderBottom: '1px solid #e5e7eb' }}>
                        <b>{r.medication}</b>{r.sig ? <Box component="span" sx={{ color: '#374151' }}> — {r.sig}</Box> : null}
                      </Box>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Section>
          )}

          {data.plan && (
            <Section title="Plan and instructions">
              <Typography sx={{ fontSize: 13.5, whiteSpace: 'pre-wrap' }}>{data.plan}</Typography>
            </Section>
          )}

          {data.follow_up && (
            <Section title="Follow-up">
              <Typography sx={{ fontSize: 13.5, whiteSpace: 'pre-wrap' }}>{data.follow_up}</Typography>
            </Section>
          )}

          {data.patient_summary && (
            <Section title="Your visit in plain words">
              <Typography sx={{ fontSize: 13.5, whiteSpace: 'pre-wrap' }}>{data.patient_summary}</Typography>
            </Section>
          )}

          <Box sx={{ mt: 5, display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ textAlign: 'center', minWidth: 220 }}>
              <Box sx={{ borderTop: '1px solid #9ca3af', pt: 0.5 }} />
              <Typography sx={{ fontSize: 12.5 }}>{data.visit?.doctor_name}</Typography>
              <Typography sx={{ fontSize: 11, color: '#6b7280' }}>Signature</Typography>
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 3, pt: 1.5, borderTop: '1px solid #d1d5db', textAlign: 'center' }}>
          {lh.footer_text && (
            <Typography sx={{ fontSize: 11.5, color: '#374151', whiteSpace: 'pre-line', mb: 0.75 }}>{lh.footer_text}</Typography>
          )}
          <Typography sx={{ fontSize: 10.5, color: '#9ca3af', letterSpacing: 0.3 }}>
            {lh.powered_by || 'Powered by ScribeLive'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
